import DownloadIcon from '@mui/icons-material/Download';
import PropTypes from 'prop-types';

import Button from '@zenbusiness/zui/Button';
import { superstyled } from '@zenbusiness/zui/styles';

import { CLICKSTREAM_COMMON_PROPS } from '../../../utils/constants';

export const DownloadButton = superstyled({
  name: 'ReportDownloadButton',
  slot: 'DownloadButton',
  tag: Button,
  styles: ({ theme }) => ({
    alignSelf: 'center',
    padding: theme.spacing(1.5, 2)
  })
});

/**
 * Reusable download button for report panels
 * Handles clickstream tracking and loading states
 *
 * @param {object} props - Component props
 * @param {string} props.reportTitle - Title of the report for clickstream tracking
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {boolean} [props.loading=false] - Whether button is in loading state
 * @param {Function} props.onClick - Click handler
 * @param {string} [props.size='small'] - Button size ('small', 'medium', 'large')
 * @param {string} [props.variant='contained'] - Button variant
 * @param {string} [props.color='primary'] - Button color
 * @param {string} [props.buttonText='Download report'] - Button text
 * @returns {import('react').ReactElement} The download button component
 */
function ReportDownloadButton({
  reportTitle,
  buttonText = 'Download report',
  disabled = false,
  loading = false,
  onClick,
  size = 'small',
  variant = 'contained',
  color = 'primary'
}) {
  // Generate element name from report title: "My Report Title" -> "my_report_title_download"
  const elementName = `${reportTitle?.toLowerCase().replace(/\s+/g, '_')}_download`;

  return (
    <DownloadButton
      clickstreamProps={{
        ...CLICKSTREAM_COMMON_PROPS,
        elementName,
        elementText: reportTitle
      }}
      color={color}
      disabled={disabled}
      loading={loading}
      size={size}
      startIcon={<DownloadIcon />}
      variant={variant}
      onClick={onClick}
    >
      {buttonText}
    </DownloadButton>
  );
}

ReportDownloadButton.propTypes = {
  reportTitle: PropTypes.string, // Optional - will use fallback if not provided
  buttonText: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.string,
  color: PropTypes.string
};

export default ReportDownloadButton;
